import { grants } from '@/lib/permissions/grants'

export interface PermissionHolder {
  id: string
  permissions: readonly string[]
}

export interface WorkspacePermissionHolder extends PermissionHolder {
  organizationId: string
}

/** The loaded organizations/workspaces and the current selection, as seen by permission checks. */
export interface PermissionSnapshot {
  organizations: readonly PermissionHolder[]
  workspaces: readonly WorkspacePermissionHolder[]
  currentOrganizationId: string | null
  currentWorkspaceId: string | null
}

/**
 * Where a permission is checked, mirroring the backend's `CheckPermissionTask`:
 * - `'current'`: the selected workspace (its own grants plus its organization's), or the
 *   selected organization when it has no workspace;
 * - `{ workspaceId }`: that workspace, with its organization's grants cascading down;
 * - `{ organizationId }`: that organization only;
 * - `'anywhere'`: a grant at any organization or workspace is enough.
 */
export type PermissionScope =
  'current' | 'anywhere' | { organizationId: string } | { workspaceId: string }

function organizationGrants(snapshot: PermissionSnapshot, organizationId: string): string[] {
  return (
    snapshot.organizations.find((item) => item.id === organizationId)?.permissions.slice() ?? []
  )
}

function workspaceGrants(snapshot: PermissionSnapshot, workspaceId: string): string[] {
  const workspace = snapshot.workspaces.find((item) => item.id === workspaceId)

  return workspace
    ? [...workspace.permissions, ...organizationGrants(snapshot, workspace.organizationId)]
    : []
}

/** Every permission key that applies in `scope`. */
export function heldPermissions(snapshot: PermissionSnapshot, scope: PermissionScope): string[] {
  if (scope === 'anywhere') {
    return [
      ...snapshot.organizations.flatMap((item) => item.permissions),
      ...snapshot.workspaces.flatMap((item) => item.permissions),
    ]
  }

  if (scope === 'current') {
    if (snapshot.currentWorkspaceId) return workspaceGrants(snapshot, snapshot.currentWorkspaceId)
    if (snapshot.currentOrganizationId)
      return organizationGrants(snapshot, snapshot.currentOrganizationId)

    return []
  }

  if ('workspaceId' in scope) return workspaceGrants(snapshot, scope.workspaceId)

  return organizationGrants(snapshot, scope.organizationId)
}

export function canIn(
  snapshot: PermissionSnapshot,
  permission: string,
  scope: PermissionScope = 'current',
): boolean {
  return grants(heldPermissions(snapshot, scope), permission)
}
